import { motion } from "framer-motion";
import { MapPin, Truck } from "lucide-react";
import { DELIVERY_AREAS } from "@/config";
import { SectionHeading } from "@/components/Section";
import { PortalButton } from "@/components/PortalButton";
import { Reveal, stagger, fadeUp } from "@/components/Reveal";

// approximate x/y % positions for a stylised Alberta map
const NODES = [
  { name: "Edmonton", x: 50, y: 42, primary: true },
  { name: "St. Albert", x: 44, y: 37 },
  { name: "Sherwood Park", x: 58, y: 43 },
  { name: "Spruce Grove", x: 40, y: 44 },
  { name: "Fort Saskatchewan", x: 60, y: 35 },
  { name: "Leduc", x: 49, y: 52 },
  { name: "Red Deer", x: 52, y: 66 },
  { name: "Calgary", x: 55, y: 85 },
];

export default function DeliveryMap() {
  return (
    <section data-testid="delivery-map" className="py-24 md:py-32 bg-sage/50">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading
            chapter="06 / Flexible Delivery"
            overline="Delivery That Works Around You"
            title={<>Delivery that works around your business</>}
            lead="Every restaurant has a different ordering schedule. Our team works with customers to create delivery days that fit their operations — free across Edmonton on qualifying orders, with scheduled and route delivery across Alberta."
          />
          <motion.ul variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-8">
            {DELIVERY_AREAS.map((a) => (
              <motion.li key={a.name} variants={fadeUp} className="flex items-center justify-between border-b border-forest/10 py-2">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-forest">
                  <MapPin className={`h-4 w-4 ${a.primary ? "text-butter-deep" : "text-forest/40"}`} /> {a.name}
                </span>
                <span className="text-[11px] text-forest/55 font-mono-accent">{a.note}</span>
              </motion.li>
            ))}
          </motion.ul>
          <p className="text-xs text-forest/55 mt-5 max-w-lg">
            Delivery eligibility, timing, fees and minimum orders may vary outside Edmonton.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <PortalButton variant="solid" testId="check-delivery-btn">Check Delivery Availability</PortalButton>
            <a href="/contact" data-testid="delivery-team-btn" className="inline-flex items-center gap-2 rounded-full border border-forest px-6 py-3 text-sm font-semibold text-forest hover:bg-forest hover:text-butter transition-colors duration-300">
              <Truck className="h-4 w-4" /> Speak With Our Delivery Team
            </a>
          </div>
        </div>

        {/* Stylised map */}
        <Reveal delay={0.1}>
          <div className="relative aspect-square rounded-3xl bg-forest overflow-hidden border border-forest/20 shadow-hard-lg">
            <div className="absolute inset-0 opacity-[0.15] noise-bg" />
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
              <path d="M28 18 L70 16 L74 60 L66 92 L40 90 L26 62 Z" fill="hsl(24,12%,22%)" stroke="#6abb32" strokeWidth="0.4" strokeDasharray="1.5 1.5" />
            </svg>
            {NODES.map((n) => (
              <div key={n.name} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${n.x}%`, top: `${n.y}%` }}>
                <span className="relative flex items-center justify-center">
                  {n.primary && <span className="absolute h-6 w-6 rounded-full bg-butter-deep/40 animate-ping" />}
                  <span className={`relative rounded-full ${n.primary ? "h-3.5 w-3.5 bg-butter-deep" : "h-2.5 w-2.5 bg-cream"}`} />
                </span>
                <span className={`absolute left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap text-[10px] font-mono-accent ${n.primary ? "text-butter" : "text-cream/70"}`}>{n.name}</span>
              </div>
            ))}
            <span className="absolute bottom-4 left-4 overline text-cream/50">Alberta · Delivery Network</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
